import React from 'react';

/**
 * Global Error Boundary — catches any rendering error anywhere in the React
 * tree and shows a friendly fallback UI instead of a white screen.
 * Critical for production debugging (e.g. tela branca scenarios).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Log to console for debugging in production.
    console.error('[ErrorBoundary] Caught render error:', error, info);
  }

  handleReload = () => {
    try {
      // Clear caches + SW to force a clean reload (helps after broken deploys).
      if (window.caches) {
        caches.keys().then((ks) => Promise.all(ks.map((k) => caches.delete(k))));
      }
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations()
          .then((rs) => Promise.all(rs.map((r) => r.unregister())))
          .finally(() => window.location.reload());
        return;
      }
    } catch (e) { /* ignore */ }
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errMsg = this.state.error?.message || String(this.state.error || 'Erro desconhecido');
    const stack = this.state.error?.stack || '';

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="error-boundary">
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500 text-2xl">!</div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Algo deu errado</h1>
              <p className="text-sm text-slate-500 mt-0.5">A página encontrou um erro inesperado.</p>
            </div>
          </div>

          <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100">
            <p className="text-xs text-slate-500 mb-1">Detalhes técnicos:</p>
            <p className="text-sm font-mono text-red-600 break-words" data-testid="error-message">
              {errMsg}
            </p>
          </div>

          {stack && (
            <details className="mb-4">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                Ver stack trace
              </summary>
              <pre className="mt-2 text-[10px] text-slate-600 bg-slate-50 p-2 rounded overflow-auto max-h-48 font-mono">
                {stack}
              </pre>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={this.handleReload}
              data-testid="error-reload-btn"
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Recarregar (limpa cache)
            </button>
            <button
              onClick={this.handleHome}
              data-testid="error-home-btn"
              className="flex-1 px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-lg text-sm font-medium transition-colors"
            >
              Ir para o início
            </button>
          </div>

          <p className="text-[11px] text-slate-400 text-center mt-4">
            Se o problema persistir, envie este erro para o suporte.
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
